export enum ProjectOnboardingStatus {
  DRAFT = 'DRAFT', // "Pending"
  PENDING_INFRA = 'PENDING_INFRA', // "Pending"
  INFRA_FAILED = 'INFRA_FAILED', // "Failed"
  ABANDONED = 'ABANDONED', // "Abandoned"
  INFRA_SPINED = 'INFRA_SPINED', // "Pending"
  CERT_ADDED = 'CERT_ADDED', // "Ongoing"
  CONNECTED = 'CONNECTED', // "Ongoing"
  TESTED = 'TESTED', // "Completed"
  EXPIRED = 'EXPIRED', // "Expired"
}

export const isUploadingDisabled = (status: ProjectOnboardingStatus) =>
  [
    ProjectOnboardingStatus.DRAFT,
    ProjectOnboardingStatus.PENDING_INFRA,
    ProjectOnboardingStatus.INFRA_FAILED,
    ProjectOnboardingStatus.ABANDONED,
    ProjectOnboardingStatus.EXPIRED,
  ].includes(status);

export const isCertificateAdded = (status: ProjectOnboardingStatus) =>
  [ProjectOnboardingStatus.CERT_ADDED].includes(status);

export const isCertificateConnected = (status: ProjectOnboardingStatus) =>
  [ProjectOnboardingStatus.CONNECTED].includes(status);

export const isCertificateTested = (status: ProjectOnboardingStatus) =>
  [ProjectOnboardingStatus.TESTED].includes(status);

export const isConnectionButtonShown = (project: DataProducerProject, currentUserId: string) =>
  project.vendorProjectOwnerId === currentUserId &&
  [
    ProjectOnboardingStatus.INFRA_SPINED,
    ProjectOnboardingStatus.CERT_ADDED,
    ProjectOnboardingStatus.CONNECTED,
  ].includes(project.status);

export const isProjectPending = (status: ProjectOnboardingStatus) =>
  [ProjectOnboardingStatus.DRAFT, ProjectOnboardingStatus.PENDING_INFRA, ProjectOnboardingStatus.INFRA_SPINED].includes(
    status,
  );

export const isProjectOngoing = (status: ProjectOnboardingStatus) =>
  [ProjectOnboardingStatus.CERT_ADDED, ProjectOnboardingStatus.CONNECTED].includes(status);

export const isProjectFailed = (status: ProjectOnboardingStatus) =>
  [ProjectOnboardingStatus.INFRA_FAILED, ProjectOnboardingStatus.ABANDONED].includes(status);

export const isProjectCompleted = (status: ProjectOnboardingStatus) =>
  [ProjectOnboardingStatus.TESTED].includes(status);

const SIXTY_DAYS_MILLIS = 5184000000;
export const isProjectExpired = (certTimeStamp: number) => certTimeStamp < new Date().getTime() + SIXTY_DAYS_MILLIS;

export const isProjectOnboarding = (status: ProjectOnboardingStatus) =>
  isProjectOngoing(status) || isProjectPending(status);

export interface ProjectCertificate {
  certificateArn?: string;
  certificateId?: string;
  expirationTime?: {
    notBefore?: string;
    notAfter?: string;
  };
}

export interface DataProducerProject {
  name: string;
  type: AHS | NZM;
  id: string;
  status: ProjectOnboardingStatus;
  vendorProjectOwnerEmail: string;
  vendorProjectOwnerId: string;
  mqttEndpoint?: string;
  firstName: string;
  lastName: string;
  certificate?: ProjectCertificate;
}

export type NewProjectSchema = {
  project: {
    name: string;
    status: string;
    vendorProjectOwnerEmail: string;
    type: AHS | NZM;
    firstName: string;
    lastName: string;
  };
  vendorId: string;
};

export type UpdatedProjectSchema = {
  project: Partial<Pick<DataProducerProject, 'firstName' | 'lastName' | 'vendorProjectOwnerEmail' | 'status'>>;
  vendorId: string;
  projectId: string;
};

export enum AHS {
  AHU = 'AHU',
  // ACCESS_CONTROL_EQUIPMENT = 'Access_Control_Equipment',
  // CRAC = 'CRAC',
  // CT = 'CT',
  // CHILLED_WATER_PUMP = 'Chilled_Water_Pump',
  // CHILLED_WATER_SYSTEM = 'Chilled_Water_System',
  CHILLER = 'Chiller',
  // CONDENSER_WATER_PUMP = 'Condenser_Water_Pump',
  // CONDENSER_WATER_SYSTEM = 'Condenser_Water_System',
  // COOLING_TOWER = 'Cooling_Tower',
  // DOAS = 'DOAS',
  // DAMPER = 'Damper',
  ELECTRICAL_METER = 'Electrical_Meter',
  // ELEVATOR = 'Elevator',
  // EXHAUST_FAN = 'Exhaust_Fan',
  FCU = 'FCU',
  // GAS_METER = 'Gas_Meter',
  GATEWAY = 'Gateway',
  // HX = 'HX',
  // HEATING_COIL = 'Heating_Coil',
  IAQ = 'IAQ',
  IAQ_SENSOR = 'IAQ_Sensor',
  INDUCTION_UNIT = 'Induction_Unit',
  // LIGHTING = 'Lighting',
  METER = 'Meter',
  // NATURAL_GAS_METER = 'Natural_Gas_Meter',
  // NOT_SET = 'Not_Set',
  OCCUPANCY = 'Occupancy',
  // OCCUPANCY_SENSOR_EQUIPMENT = 'Occupancy_Sensor_Equipment',
  RTU = 'RTU',
  // STEAM_METER = 'Steam_Meter',
  // SUPPLY_LOOP = 'Supply_Loop',
  // TERMINAL_UNIT = 'Terminal_Unit',
  VAV = 'VAV',
  // VRF = 'VRF',
  // WPU = 'WPU',
  // WATER_DISTRIBUTION = 'Water_Distribution',
  // WATER_METER = 'Water_Meter',
  WEATHER_STATION = 'Weather_Station',
}

export enum NZM {
  BUILDING_ELECTRICAL_METER = 'Building_Electrical_Meter',
  BUILDING_GAS_METER = 'Building_Gas_Meter',
  BUILDING_WATER_METER = 'Building_Water_Meter',
}

export interface DeviceType {
  AHS: AHS;
  NZM: NZM;
}

export const DeviceTypeDesc: Record<AHS /*| NZM*/, string> = {
  [AHS.AHU]: 'AHU',
  // [AHS.ACCESS_CONTROL_EQUIPMENT]: 'Access Control Equipment',
  // [AHS.CRAC]: 'CRAC',
  // [AHS.CT]: 'CT',
  // [AHS.CHILLED_WATER_PUMP]: 'Chilled Water Pump',
  // [AHS.CHILLED_WATER_SYSTEM]: 'Chilled Water System',
  [AHS.CHILLER]: 'Chiller',
  // [AHS.CONDENSER_WATER_PUMP]: 'Condenser Water Pump',
  // [AHS.CONDENSER_WATER_SYSTEM]: 'Condenser Water System',
  // [AHS.COOLING_TOWER]: 'Cooling Tower',
  // [AHS.DOAS]: 'DOAS',
  // [AHS.DAMPER]: 'Damper',
  [AHS.ELECTRICAL_METER]: 'Electrical Meter',
  // [AHS.ELEVATOR]: 'Elevator',
  // [AHS.EXHAUST_FAN]: 'Exhaust Fan',
  [AHS.FCU]: 'FCU',
  // [AHS.GAS_METER]: 'Gas Meter',
  [AHS.GATEWAY]: 'Gateway',
  // [AHS.HX]: 'HX',
  // [AHS.HEATING_COIL]: 'Heating Coil',
  [AHS.IAQ]: 'IAQ',
  [AHS.IAQ_SENSOR]: 'IAQ Sensor',
  [AHS.INDUCTION_UNIT]: 'Induction Unit',
  // [AHS.LIGHTING]: 'Lighting',
  [AHS.METER]: 'Meter',
  // [AHS.NATURAL_GAS_METER]: 'Natural Gas Meter',
  // [AHS.NOT_SET]: 'Not Set',
  [AHS.OCCUPANCY]: 'Occupancy',
  // [AHS.OCCUPANCY_SENSOR_EQUIPMENT]: 'Occupancy Sensor Equipment',
  [AHS.RTU]: 'RTU',
  // [AHS.STEAM_METER]: 'Steam Meter',
  // [AHS.SUPPLY_LOOP]: 'Supply Loop',
  // [AHS.TERMINAL_UNIT]: 'Terminal Unit',
  [AHS.VAV]: 'VAV',
  // [AHS.VRF]: 'VRF',
  // [AHS.WPU]: 'WPU',
  // [AHS.WATER_DISTRIBUTION]: 'Water Distribution',
  // [AHS.WATER_METER]: 'Water Meter',
  [AHS.WEATHER_STATION]: 'Weather Station',

  // [NZM.BUILDING_ELECTRICAL_METER]: 'Building Electrical Meter',
  // [NZM.BUILDING_GAS_METER]: 'Building Gas Meter',
  // [NZM.BUILDING_WATER_METER]: 'Building Water Meter',
};

export interface VendorDataContractErrorList {
  date: string;
  errorMessage: string;
  messageId: string;
  payload: string;
}

export interface ErrorMessagesForProject {
  errorCounter: number;
  vendorDataContractErrorList: [VendorDataContractErrorList];
}

export enum SORT_BY {
  DBIRTH = 'DBIRTH',
  DDATA = 'DDATA',
}
